/**
 * [BLURY] | 🥭 RatioPanel
 * Output geometry: the ratio presets, a free w:h entry and the export width.
 * Shape positions are normalised, so neither control destroys the composition.
 * @build 13.09.26
 * @updated 13.09.26 PHT
 * @author TONYTONY Sàrl
 */
import { useEffect, useState } from 'react';
import { MAX_WIDTH, MIN_WIDTH, PRESET_RATIOS, WIDTH_PRESETS } from '@/engine/ratio';
import { useStudio } from '@/state/useStudio';
import { Button } from '../ui/Button';
import { Section, SectionNote } from '../ui/Section';
import { Row, RowLabel } from '../ui/Slider';
import styles from './RatioPanel.module.css';

const SWATCH_MAX = 13;

export function RatioPanel() {
  const ratio = useStudio(s => s.ratio);
  const size = useStudio(s => s.size);
  const outputWidth = useStudio(s => s.outputWidth);
  const setRatio = useStudio(s => s.setRatio);
  const setOutputWidth = useStudio(s => s.setOutputWidth);

  const [draft, setDraft] = useState({ w: String(ratio.w), h: String(ratio.h) });
  const [widthDraft, setWidthDraft] = useState(String(outputWidth));

  useEffect(() => {
    setDraft({ w: String(ratio.w), h: String(ratio.h) });
  }, [ratio]);

  useEffect(() => {
    setWidthDraft(String(outputWidth));
  }, [outputWidth]);

  const current = ratio.w / ratio.h;

  const commit = (next: { w: string; h: string }) => {
    setDraft(next);
    const w = Number(next.w);
    const h = Number(next.h);
    if (!w || !h) return;
    setRatio(w, h);
  };

  const commitWidth = (value: string) => {
    setWidthDraft(value);
    const w = Number(value);
    if (!w) return;
    setOutputWidth(w);
  };

  return (
    <Section badge={0} title="Canvas">
      <div className={styles.ratios}>
        {PRESET_RATIOS.map(preset => {
          const k = SWATCH_MAX / Math.max(preset.w, preset.h);
          const isCurrent = preset.w / preset.h === current;
          return (
            <Button
              key={`${preset.w}:${preset.h}`}
              className={styles.ratio}
              active={isCurrent}
              onClick={() => setRatio(preset.w, preset.h)}
            >
              <b
                style={{
                  width: `${(preset.w * k).toFixed(1)}px`,
                  height: `${(preset.h * k).toFixed(1)}px`,
                  opacity: isCurrent ? 1 : 0.55,
                }}
              />
              {preset.w}:{preset.h}
            </Button>
          );
        })}
      </div>

      <div className={styles.spacer}>
        <Row>
          <RowLabel name="custom ratio" value={`${size.width} × ${size.height}`} />
          <div className={styles.custom}>
            <input
              type="number"
              min={1}
              max={64}
              step={0.5}
              aria-label="ratio width"
              value={draft.w}
              onChange={e => commit({ ...draft, w: e.target.value })}
            />
            <em>:</em>
            <input
              type="number"
              min={1}
              max={64}
              step={0.5}
              aria-label="ratio height"
              value={draft.h}
              onChange={e => commit({ ...draft, h: e.target.value })}
            />
          </div>
        </Row>

        <Row>
          <RowLabel name="output width" value={`${outputWidth} px`} />
          <input
            className={styles.widthField}
            type="number"
            min={MIN_WIDTH}
            max={MAX_WIDTH}
            step={10}
            aria-label="output width"
            value={widthDraft}
            onChange={e => commitWidth(e.target.value)}
            onBlur={() => setWidthDraft(String(outputWidth))}
          />
          <div className={styles.widths}>
            {WIDTH_PRESETS.map(preset => (
              <Button
                key={preset}
                active={preset === outputWidth}
                onClick={() => setOutputWidth(preset)}
              >
                {preset}
              </Button>
            ))}
          </div>
        </Row>
      </div>

      <SectionNote>
        Width sets the exported file; height follows the ratio. The preview stays capped at 1200 px
        on its long edge, and blur and grain are scaled on export so the file matches what you see.
      </SectionNote>
    </Section>
  );
}
